import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from '@/domains/internal/auth/hooks/useAuth';
import { useAuth as useAuthContext } from './AuthContext';
import { paymentService } from '@/domains/internal/payments/services/paymentService';
import { usePaymentUpdates } from './PaymentUpdatesContext';
import { logger } from '@/utils/logger';

interface PendingApprovalsContextType {
  pendingCount: number;
  refreshCount: () => Promise<void>;
}

const PendingApprovalsContext = createContext<PendingApprovalsContextType | undefined>(undefined);

export const PendingApprovalsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const { isSuperAdmin, user } = useAuth();
  const { isAuthReady, isAuthenticated } = useAuthContext();
  const { subscribe } = usePaymentUpdates();

  // Memoize user identifiers to prevent unnecessary re-renders
  const userType = useMemo(() => user?.user_type, [user?.user_type]);
  const staffId = useMemo(() => user && 'staff_id' in user ? user.staff_id : null, [user]);
  const isSuperAdminValue = useMemo(() => isSuperAdmin(), [isSuperAdmin]);

  // Use useCallback to create a stable function reference
  const fetchPendingCount = useCallback(async () => {
    // Wait for auth to be ready
    if (!isAuthReady) {
      logger.debug('[PendingApprovals] Waiting for auth to be ready...');
      return;
    }

    // Skip if not authenticated
    if (!isAuthenticated) {
      setPendingCount(0);
      return;
    }

    // Skip if no user is logged in (e.g., on login page)
    if (!user) {
      setPendingCount(0);
      return;
    }

    // Skip API calls for referral users and team members - they don't have access to refund approvals
    if (user.user_type === 'referral' || user.user_type === 'referral_team') {
      setPendingCount(0);
      return;
    }

    // isSuperAdmin is a function, need to call it
    if (!isSuperAdminValue) {
      setPendingCount(0);
      return;
    }

    try {
      const data = await paymentService.getRefundApprovals();
      const pending = data.approvals.filter((a: any) => a.status === 'Pending').length;
      setPendingCount(pending);
    } catch (error) {
      logger.error('[PendingApprovals] Failed to fetch pending approvals count:', error);
    }
  }, [isAuthReady, isAuthenticated, user, isSuperAdminValue]); // Stable dependencies

  // Fetch count when user type or staff ID changes
  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount, userType, staffId]);

  // Subscribe to payment updates ONCE
  const subscribeRef = useRef(subscribe);
  useEffect(() => {
    subscribeRef.current = subscribe;
  }, [subscribe]);

  useEffect(() => {
    const unsubscribe = subscribeRef.current((message) => {
      // Refresh count when refund-related events occur
      if (
        message.type === 'refund_approval_requested' ||
        message.type === 'refund_approved' ||
        message.type === 'refund_rejected'
      ) {
        fetchPendingCount();
      }
    });

    return unsubscribe;
  }, [fetchPendingCount]); // Only re-subscribe when fetchPendingCount changes

  return (
    <PendingApprovalsContext.Provider value={{ pendingCount, refreshCount: fetchPendingCount }}>
      {children}
    </PendingApprovalsContext.Provider>
  );
};

export const usePendingApprovals = () => {
  const context = useContext(PendingApprovalsContext);
  if (context === undefined) {
    throw new Error('usePendingApprovals must be used within a PendingApprovalsProvider');
  }
  return context;
};
